/**
 * ``AnnotationDetailPanel`` — the selected annotation's label/style config,
 * rendered inside the Entity Details panel (ADR-034 design update 2026-06-11,
 * PR C — replaces the floating editor). Shown when an annotation is selected
 * (the annotation tool store's ``selectedId``), taking precedence over the
 * inspected entity so the operator focuses one thing at a time.
 *
 * Edits a local draft (label + style), **Apply**-s via :func:`useUpdateAnnotation`
 * (PUT, preserving geometry), and **Delete**-s via :func:`useDeleteAnnotation`
 * (clearing the selection). A fresh draft per selection via a keyed remount.
 *
 * The expanded per-kind controls — the dash dropdown, the granular measurement
 * checkboxes (incl. circumference) — land in PR C2; this PR moves the existing
 * controls into the panel unchanged.
 *
 * Cross-references:
 *   - ADR-034 design update; feature-spec §8.2.
 *   - @/annotations/{use-annotations, annotation-tool-store, annotation-update}.
 */
import { useState } from "react";
import { useAnnotations, useUpdateAnnotation } from "@/annotations/use-annotations";
import type { Annotation } from "@/annotations/use-annotations";
import { useAnnotationDeleteConfirmStore } from "@/annotations/annotation-delete-confirm-store";
import {
  buildAnnotationWriteBody,
  type AnnotationLabel,
  type AnnotationStyle,
} from "@/annotations/annotation-update";
import { MEASUREMENTS_BY_KIND, type MeasurementKey } from "@/annotations/annotation-label";

const FIELD_CLASS = "flex flex-col gap-1 text-body-sm text-text-primary";
const TEXT_INPUT_CLASS = "rounded border border-border bg-bg-base px-2 py-1 text-text-primary";
const ROW_CLASS = "flex items-center justify-between gap-2 text-body-sm text-text-primary";
/** Fallback colour for nullable style colours (matches the draw default). */
const FALLBACK_COLOR = "#f5a623";

/** Display labels for the measurement checkboxes. */
const MEASUREMENT_LABELS: Record<MeasurementKey, string> = {
  length: "Length",
  azimuth: "Azimuth",
  area: "Area",
  radius: "Radius",
  circumference: "Circumference",
  perimeter: "Perimeter",
  location: "Location",
};

/** The dash-style options (non-marker outlines). */
type StrokeDash = NonNullable<AnnotationStyle["stroke_dash"]>;
const DASH_OPTIONS: readonly { value: StrokeDash; label: string }[] = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
  { value: "dash_dot", label: "Dash-dot" },
];

/** The arrowhead options (line ends, start + end independently). The open-chevron
 *  ``"arrow"`` style was dropped 2026-06-11 (it SDF'd poorly at icon size); the
 *  schema keeps the enum value, and a legacy ``"arrow"`` is shown + saved as a
 *  triangle. */
type ArrowHead = NonNullable<AnnotationStyle["arrow_start"]>;
const ARROW_OPTIONS: readonly { value: ArrowHead; label: string }[] = [
  { value: "none", label: "None" },
  { value: "triangle", label: "Triangle" },
];
/** Normalise a stored arrowhead to a selectable option (legacy ``arrow`` → triangle). */
function arrowValue(value: ArrowHead | null | undefined): ArrowHead {
  return value === "arrow" ? "triangle" : (value ?? "none");
}

/** The config form for one annotation. Keyed on the id so a new selection
 *  starts from a fresh draft. */
function AnnotationConfigForm({ annotation }: { annotation: Annotation }): React.ReactElement {
  const updateAnnotation = useUpdateAnnotation();
  const requestDelete = useAnnotationDeleteConfirmStore((state) => state.requestDelete);
  const [label, setLabel] = useState<AnnotationLabel>(annotation.label);
  const [style, setStyle] = useState<AnnotationStyle>(annotation.style);

  const { kind } = annotation;
  const showStroke = kind !== "marker";
  const showFill = kind === "circle" || kind === "polygon";
  const showMarker = kind === "marker";
  const showArrows = kind === "line";

  // ``null`` measurements = all available for the kind; toggling makes the list
  // explicit (consumed by the readout — annotation-label.ts).
  const measurements = label.measurements ?? MEASUREMENTS_BY_KIND[kind];
  const toggleMeasurement = (key: MeasurementKey, checked: boolean): void => {
    const next = checked
      ? Array.from(new Set([...measurements, key]))
      : measurements.filter((item) => item !== key);
    setLabel({ ...label, measurements: next });
  };

  const apply = (): void => {
    updateAnnotation.mutate({
      annotationId: annotation.annotation_id,
      body: buildAnnotationWriteBody(annotation, label, style),
    });
  };
  const remove = (): void => {
    requestDelete(annotation.annotation_id);
  };

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4" data-testid="annotation-config">
      <label className={FIELD_CLASS}>
        Label
        <input
          type="text"
          data-testid="annotation-config-text"
          value={label.text}
          maxLength={120}
          onChange={(event) => setLabel({ ...label, text: event.target.value })}
          className={TEXT_INPUT_CLASS}
        />
      </label>

      <fieldset className="flex flex-col gap-1 text-body-sm text-text-primary">
        <legend className="text-text-secondary">Measurements</legend>
        {MEASUREMENTS_BY_KIND[kind].map((key) => (
          <label key={key} className="flex items-center gap-2">
            <input
              type="checkbox"
              data-testid={`annotation-config-measure-${key}`}
              checked={measurements.includes(key)}
              onChange={(event) => toggleMeasurement(key, event.target.checked)}
            />
            {MEASUREMENT_LABELS[key]}
          </label>
        ))}
        {/* #8: show the measurement readout only on hover (a tooltip) vs always
            on the map. The text label is unaffected (always on the map). */}
        <label className="mt-1 flex items-center gap-2 border-t border-border pt-1">
          <input
            type="checkbox"
            data-testid="annotation-config-measure-on-hover"
            checked={(label.measurement_display ?? "on_map") === "hover"}
            onChange={(event) =>
              setLabel({
                ...label,
                measurement_display: event.target.checked ? "hover" : "on_map",
              })
            }
          />
          Show on hover only
        </label>
      </fieldset>

      {showStroke && (
        <div className={ROW_CLASS}>
          <span>Line</span>
          <input
            type="color"
            aria-label="Stroke color"
            data-testid="annotation-config-stroke-color"
            value={style.stroke_color}
            onChange={(event) => setStyle({ ...style, stroke_color: event.target.value })}
          />
          <label className="flex items-center gap-1 text-text-secondary">
            Weight
            <input
              type="range"
              aria-label="Line weight"
              min={1}
              max={8}
              step={1}
              value={style.stroke_width_px}
              onChange={(event) =>
                setStyle({ ...style, stroke_width_px: Number(event.target.value) })
              }
            />
          </label>
        </div>
      )}

      {showStroke && (
        <label className={FIELD_CLASS}>
          Line style
          <select
            data-testid="annotation-config-dash"
            value={style.stroke_dash ?? "solid"}
            onChange={(event) =>
              setStyle({ ...style, stroke_dash: event.target.value as StrokeDash })
            }
            className={TEXT_INPUT_CLASS}
          >
            {DASH_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {showArrows && (
        <div className="flex gap-2 text-body-sm text-text-primary">
          <label className="flex flex-1 flex-col gap-1">
            Start arrow
            <select
              data-testid="annotation-config-arrow-start"
              value={arrowValue(style.arrow_start)}
              onChange={(event) =>
                setStyle({ ...style, arrow_start: event.target.value as ArrowHead })
              }
              className={TEXT_INPUT_CLASS}
            >
              {ARROW_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1">
            End arrow
            <select
              data-testid="annotation-config-arrow-end"
              value={arrowValue(style.arrow_end)}
              onChange={(event) =>
                setStyle({ ...style, arrow_end: event.target.value as ArrowHead })
              }
              className={TEXT_INPUT_CLASS}
            >
              {ARROW_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {showFill && (
        <div className={ROW_CLASS}>
          <span>Fill</span>
          <input
            type="color"
            aria-label="Fill color"
            data-testid="annotation-config-fill-color"
            value={style.fill_color ?? FALLBACK_COLOR}
            onChange={(event) => setStyle({ ...style, fill_color: event.target.value })}
          />
          <label className="flex items-center gap-1 text-text-secondary">
            Opacity
            <input
              type="range"
              aria-label="Fill opacity"
              min={0}
              max={1}
              step={0.05}
              value={style.fill_opacity}
              onChange={(event) => setStyle({ ...style, fill_opacity: Number(event.target.value) })}
            />
          </label>
        </div>
      )}

      {showMarker && (
        <div className={ROW_CLASS}>
          <span>Marker</span>
          <input
            type="color"
            aria-label="Marker color"
            data-testid="annotation-config-marker-color"
            value={style.marker_color ?? FALLBACK_COLOR}
            onChange={(event) => setStyle({ ...style, marker_color: event.target.value })}
          />
          <label className="flex items-center gap-1 text-text-secondary">
            Size
            <input
              type="range"
              aria-label="Marker size"
              min={6}
              max={24}
              step={1}
              value={style.marker_size_px ?? 6}
              onChange={(event) =>
                setStyle({ ...style, marker_size_px: Number(event.target.value) })
              }
            />
          </label>
        </div>
      )}

      <div className="mt-1 flex gap-2">
        <button
          type="button"
          data-testid="annotation-config-apply"
          onClick={apply}
          className="flex-1 rounded bg-brand px-2 py-1 text-body-sm text-bg-base hover:opacity-90"
        >
          Apply
        </button>
        <button
          type="button"
          data-testid="annotation-config-delete"
          onClick={remove}
          className="rounded border border-border px-2 py-1 text-body-sm text-destructive hover:bg-bg-elevated"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/** The Entity Details surface for a selected annotation (header + config form). */
export function AnnotationDetailPanel({ id }: { id: string }): React.ReactElement {
  const annotationsQuery = useAnnotations();
  const annotation = annotationsQuery.data?.find((item) => item.annotation_id === id) ?? null;

  return (
    <div
      data-testid="entity-details-panel"
      data-kind="annotation"
      role="region"
      aria-label="Annotation details"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <header className="flex items-start justify-between border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2
            className="text-body font-medium capitalize text-text-primary"
            data-testid="entity-details-title"
          >
            {annotation !== null ? `${annotation.kind} annotation` : "Annotation"}
          </h2>
          <p className="mt-0.5 text-body-sm text-text-secondary">
            Map annotation · {id.slice(0, 8)}…
          </p>
        </div>
      </header>
      {annotation === null ? (
        <p className="p-4 text-body-sm text-text-muted">Annotation not found.</p>
      ) : (
        <AnnotationConfigForm key={annotation.annotation_id} annotation={annotation} />
      )}
    </div>
  );
}
